/**
 * sessionRestore.js
 *
 * The other half of a reconnect. `zapAll` with `reason: 'reconnect'` says the
 * workspace is being torn down so it can be rebuilt; this says whether the
 * rebuild actually happened.
 *
 * A panel that spares its state across a reconnect zap has, until this arrives,
 * state describing a workspace that is not there. Tagging the zap before the
 * outcome is known is unavoidable — the zap comes first — so the outcome has to
 * be published separately rather than inferred. `reconnectAndRestoreSession` has
 * three ways to end without a restored workspace: the capture failed, the new
 * session could not be established, or the restore came back with errors. All
 * three publish `Unavailable` with a reason a person can read.
 *
 * A leaf module: the publisher (`server/connection_utils`) and the subscribers
 * both import it, and it must not pull either of them in.
 */

/** EventBroker event carrying the outcome of a reconnect's restore. */
export const SESSION_RESTORE_EVENT = 'sessionRestore';

/** @typedef {'restored'|'unavailable'} SessionRestoreStatusValue */

export const SessionRestoreStatus = {
    /** The workspace came back. Whatever was spared across the zap is valid again. */
    Restored: 'restored',
    /**
     * The workspace did not come back, or came back incomplete. Anything spared
     * across the zap now describes something that is not on screen.
     */
    Unavailable: 'unavailable',
};

/**
 * Why the workspace is not available, or null when it was restored.
 *
 * Unknown payloads count as unavailable: a panel gating a destructive or
 * dispatching action on this must fail closed, not open.
 *
 * @param {{status?: SessionRestoreStatusValue, reason?: string}} [eventData]
 * @returns {string|null}
 */
export function sessionRestoreUnavailableReason(eventData) {
    if (eventData?.status === SessionRestoreStatus.Restored) return null;
    return eventData?.reason || 'This workspace was not restored after the session reconnected.';
}
